import styled from "styled-components";

import { motion } from "../../AuthNew/shared/motionUtils";

// ============================================================================
// MODAL & OVERLAY COMPONENTS
// ============================================================================

export const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.main.overlayBackground};
  backdrop-filter: blur(6px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

export const Modal = styled(motion.div)`
  position: relative;
  width: ${({ width }) => width || "auto"};
  min-width: 400px;
  max-width: ${({ maxWidth }) => maxWidth || "720px"};
  background: ${({ theme }) => theme.colors.main.white};
  border-radius: 16px;
  box-shadow: 0 20px 60px ${({ theme }) => theme.colors.main.modalBoxShadow};
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media screen and (max-width: 768px) {
    max-width: 100%;
    min-width: auto;
    width: 100%;
  }
`;

export const ModalContent = styled.div`
  padding: ${({ padding }) => padding || "40px"};
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => gap || "32px"};
  position: relative;
  overflow: hidden; /* Prevent content overflow during transitions */

  @media screen and (max-width: 576px) {
    padding: 32px 24px;
    gap: 24px;
  }
`;

// Enhanced content wrapper with smooth transitions
export const StepContent = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

export const ModalContentWrapper = styled.div`
  overflow: hidden;
`;

// ============================================================================
// PROGRESS BAR COMPONENTS
// ============================================================================

export const ProgressBarContainer = styled.div`
  width: 100%;
  height: 10px;
  background: #eef1f7;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: ${({ marginBottom }) => marginBottom || "0"};
  position: relative;
`;

export const ProgressBarFill = styled(motion.div)`
  height: 100%;
  background: #aa8ef6;
  border-radius: 4px;
  transition: width 0.5s ease-in-out;
  position: absolute;
  top: 0;
  left: 0;
`;

// ============================================================================
// TYPOGRAPHY COMPONENTS
// ============================================================================

export const ModalTitle = styled.h2`
  font-family: "Mona Sans", sans-serif;
  font-size: 32px;
  font-weight: 700;
  line-height: 48px;
  color: #000e2a;
  margin: 0 0 8px 0;
  text-align: left;

  @media screen and (max-width: 576px) {
    font-size: 24px;
    line-height: 36px;
  }
`;

export const ModalSubtitle = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  color: #353a46;
  margin: 0;
  text-align: left;

  @media screen and (max-width: 576px) {
    font-size: 13px;
    line-height: 19.5px;
  }
`;

export const SectionTitle = styled.h3`
  font-family: "Mona Sans", sans-serif;
  font-size: ${({ fontSize }) => fontSize || "14px"};
  font-weight: ${({ fontWeight }) => fontWeight || 700};
  line-height: 21px;
  color: ${({ color }) => color || "#000e2a"};
  margin: ${({ margin }) => margin || "0 0 12px 0"};

  @media screen and (max-width: 576px) {
    font-size: ${({ mobileFontSize }) => mobileFontSize || "14px"};
  }
`;

export const SectionSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.main.authGray};
  margin: 0;
  line-height: 1.5;
`;

// ============================================================================
// INPUT COMPONENTS
// ============================================================================

export const Input = styled.input`
  width: 100%;
  height: 40px;
  padding: 10px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? "#ff5722" : "#999faa")};
  border-radius: ${({ hasError }) => (hasError ? "12px 12px 0 0" : "12px")};
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: ${({ hasError }) => (hasError ? 500 : 400)};
  line-height: 21px;
  color: #000e2a;
  background: white;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &::placeholder {
    color: #666e7f;
    font-family: "Mona Sans", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
  }

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? "#ff5722" : "#2266ef")};
    box-shadow: 0 0 0 3px
      ${({ hasError }) =>
        hasError ? "rgba(255, 87, 34, 0.1)" : "rgba(34, 102, 239, 0.1)"};
  }

  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
  }
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
`;

export const InputLabel = styled.label`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: #374151;
`;

export const InputError = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 4px;
  background: #ff5722;
  color: white;
  font-family: "Mona Sans", sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  padding: 4px 8px;
  border-radius: 0 0 8px 8px;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
`;

// ============================================================================
// BUTTON COMPONENTS
// ============================================================================

export const SecondaryButton = styled(motion.button)`
  height: ${({ height }) => height || "48px"};
  padding: ${({ padding }) => padding || "0 25px"};
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border.secondaryLight};
  border-radius: 8px;
  font-size: ${({ fontSize }) => fontSize || "14px"};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.main.authTextSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  min-width: fit-content;

  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  margin-top: ${({ marginTop }) => marginTop || "0"};
`;

// ============================================================================
// CHIP/TAG COMPONENTS (for suggestions and multi-select)
// ============================================================================

export const ChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 15px;
`;

export const Chip = styled(motion.button)`
  padding: 6px 10px;
  background-color: ${({ theme }) => theme.colors.main.authBackground};
  border: 1px solid
    ${({ isSelected, theme }) =>
      isSelected
        ? theme.colors.main.authTextSecondary
        : theme.colors.main.authBackground};
  border-radius: 24px;
  font-size: 11.5px;
  font-weight: ${({ theme, isSelected }) =>
    isSelected ? theme.fontWeights.regular : theme.fontWeights.light};
  color: ${({ theme }) => theme.colors.main.authTextSecondary};
  cursor: pointer;

  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    border-color: ${({ theme }) => theme.colors.main.authTextTertiary};
  }
`;

// ============================================================================
// SELECTION BUTTON COMPONENTS (for team size, work type)
// ============================================================================

export const SelectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  width: 100%;
`;

export const SelectionButton = styled(motion.button)`
  min-height: 48px;
  padding: 8px 12px;
  background: ${({ isSelected, theme }) =>
    isSelected ? theme.colors.main.authPurple : theme.colors.main.white};
  border: 2px solid
    ${({ isSelected, theme }) =>
      isSelected
        ? theme.colors.main.authPurple
        : theme.colors.border.secondaryLight};
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: ${({ theme, isSelected }) =>
    isSelected ? theme.fontWeights.semibold : theme.fontWeights.medium};
  color: ${({ isSelected, theme }) =>
    isSelected ? theme.colors.main.white : theme.colors.main.authTextSecondary};
  cursor: pointer;

  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    border-color: ${({ isSelected, theme }) =>
      isSelected
        ? theme.colors.main.authPurple
        : theme.colors.border.secondary};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    opacity: ${({ isSelected }) => (isSelected ? 1 : 0)};
    transition: opacity 0.2s ease;
  }
`;

// ============================================================================
// INVITATION CARD COMPONENTS
// ============================================================================

export const InvitationCard = styled(motion.div)`
  padding: 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #2684ff;
    box-shadow: 0 2px 8px rgba(38, 132, 255, 0.1);
  }
`;

export const InvitationIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ color }) => color || "#eff6ff"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-weight: bold;
  font-size: 16px;
  color: #2684ff;
  text-transform: uppercase;
`;

export const InvitationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const InvitationTitle = styled.h4`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: #1e2223;
  margin: 0 0 4px 0;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const InvitationSubtitle = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
`;

export const SelectedInvitationCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: #f8faff;
  border: 1px solid #2684ff;
  border-radius: 8px;
  margin-bottom: 16px;
`;

export const BackToCreateButton = styled.button`
  font-size: 14px;
  color: #2684ff;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;

  &:hover {
    color: #1d4ed8;
  }
`;

// ============================================================================
// LINK SHARE COMPONENTS
// ============================================================================

export const LinkShareContainer = styled.div`
  padding: ${({ padding }) => padding || "20px"};
  background: #f9fafb;
  border: 1px solid ${({ theme }) => theme.colors.border.secondaryLight};
  border-radius: 12px;
  margin-top: ${({ marginTop }) => marginTop || "20px"};
`;

export const LinkShareHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

export const LinkShareIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
  font-size: 24px;

  @media screen and (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }
`;

export const LinkShareText = styled.div`
  flex: 1;
`;

export const LinkShareTitle = styled.h4`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: #1e2223;
  margin: 0 0 4px 0;
`;

export const LinkShareDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

export const LinkInputGroup = styled.div`
  display: flex;
  gap: 8px;
`;

export const LinkInput = styled.input`
  flex: 1;
  height: 44px;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #1e2223;
  background: white;

  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #2684ff;
  }
`;

export const CopyButton = styled(motion.button)`
  height: 44px;
  padding: 0 20px;
  background: #10b981;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: white;
  cursor: pointer;

  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #059669;
  }
`;

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #e5e7eb;
  margin: ${({ margin }) => margin || "24px 0"};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "${({ text }) => text || "Or"}";
    position: absolute;
    padding: 0 12px;
    background: white;
    font-size: 13px;
    color: #9ca3af;
  }
`;

// ============================================================================
// EMAIL INPUT COMPONENTS (for invitations)
// ============================================================================

export const EmailInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const EmailInputRow = styled.div`
  display: flex;
  gap: 8px;
`;

export const AddEmailButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: #2684ff;
  cursor: pointer;

  transition: all 0.2s ease;
  margin-top: 8px;

  &:hover {
    background: #eff6ff;
  }
`;

// ============================================================================
// EMAIL CHIP COMPONENTS
// ============================================================================

export const EmailChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.colors.main.error : theme.colors.main.authBlue};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.main.whiteBg};
  min-height: 48px;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: ${({ theme, hasError }) =>
      !hasError && theme.colors.main.authBlue};
    box-shadow: ${({ theme, hasError }) =>
      !hasError && `0 0 0 3px ${theme.colors.main.blueShadow}`};
  }

  &:hover {
    border-color: ${({ theme, hasError }) =>
      !hasError && theme.colors.border.hover};
  }
`;

export const EmailChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: ${({ isValid, theme }) =>
    isValid ? theme.colors.main.authBackground : theme.colors.main.errorLight};
  color: ${({ isValid, theme }) =>
    isValid ? theme.colors.main.authTextSecondary : theme.colors.main.error};
  border-radius: 16px;
  font-size: 12.5px;
  font-weight: 500;
  animation: slideIn 0.2s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

export const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border: none;
  background: ${({ theme }) => theme.colors.main.whiteBg};
  color: ${({ isValid, theme }) =>
    isValid ? theme.colors.main.authTextTertiary : theme.colors.main.error};
  border-radius: 50%;
  cursor: pointer;
  font-size: 12.5px;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ isValid, theme }) =>
      isValid ? theme.colors.main.primary : theme.colors.main.error};
    color: ${({ theme }) => theme.colors.main.whiteBg};
    transform: scale(1.1);
  }
`;

export const EmailInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.main.textPrimary};
  min-width: 120px;

  &::placeholder {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.main.textTertiary};
  }
`;

export const EmailStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  font-size: 12px;
`;

export const ValidCount = styled.span`
  color: ${({ theme }) => theme.colors.main.success};
  font-weight: 500;
`;

export const InvalidCount = styled.span`
  color: ${({ theme }) => theme.colors.main.error};
  font-weight: 500;
`;

// ============================================================================
// LOADING COMPONENTS
// ============================================================================

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 42px;

  @media (max-width: 768px) {
    padding: 32px;
  }
  @media (max-width: 480px) {
    padding: 24px 16px;
  }
`;

export const LoadingIcon = styled(motion.div)`
  width: 80px;
  height: 80px;
  border-radius: 16px;
  background: linear-gradient(to right, #2266ef 0%, #6c2ef2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 40px;
`;

export const LoadingText = styled.h3`
  font-size: 24px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: #1e2223;
  margin: 0;

  text-align: center;
`;

export const LoadingSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 400px;
  padding-left: 40px;

  @media (max-width: 768px) {
    padding-left: 20px;
  }
  @media (max-width: 480px) {
    padding-left: 0;
  }
`;

export const LoadingStep = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  color: ${({ isComplete, theme }) =>
    isComplete ? "#2266ef" : theme.colors.main.authGray};
`;

export const LoadingStepIcon = styled(motion.div)`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ isComplete }) =>
    isComplete
      ? "linear-gradient(to right, #2266ef 0%, #6c2ef2 100%)"
      : "#e5e7eb"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  flex-shrink: 0;
`;

// ============================================================================
// INFO MESSAGE COMPONENTS
// ============================================================================

export const InfoMessage = styled(motion.div)`
  padding: 12px 0;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.main.authTextTertiary};
  margin-top: ${({ marginTop }) => marginTop || "10px"};

  line-height: 1.5;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

export const PhoneInputWrapper = styled.div`
  .PhoneInput {
    display: flex;
    align-items: center;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 4px;
    transition: border-color 0.2s;

    &:focus-within {
      border-color: #2684ff;
    }

    ${props =>
      props.hasError &&
      css`
        border-color: #ef4444;
      `}
  }

  .PhoneInputInput {
    flex: 1;
    border: none;
    padding: 12px 16px;
    font-size: 14px;
    outline: none;

    &::placeholder {
      color: #9ca3af;
    }
  }

  .PhoneInputCountry {
    margin-right: 12px;
    display: flex;
    align-items: center;
  }

  .PhoneInputCountrySelect {
    border: none;
    background: transparent;
    font-size: 14px;
    cursor: pointer;
    outline: none;
  }
`;

// ============================================================================
// ERROR SCREEN COMPONENTS
// ============================================================================

export const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

export const ErrorContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 48px 32px;
  max-width: 480px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

export const ErrorIcon = styled.div`
  margin-bottom: 24px;

  svg {
    margin: 0 auto;
  }
`;

export const ErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 12px 0;
`;

export const ErrorMessage = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

export const ErrorActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

export const RetryButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 24px;
  background: #2684ff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #0052cc;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const SkipButton = styled(motion.button)`
  width: 100%;
  padding: 12px 24px;
  background: transparent;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
    color: #374151;
  }
`;

export const HelpText = styled.p`
  font-size: 13px;
  color: #9ca3af;
  margin: 16px 0 0 0;
`;

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================

export const fadeInOut = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

// Clean, simple slide animation - no exit animation at all
export const slideInRight = {
  initial: { opacity: 0, x: 30 },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
  // No exit animation defined - let AnimatePresence handle it
};

// Smooth container transitions
export const containerTransition = {
  layout: {
    duration: 0.4,
    ease: [0.25, 0.46, 0.45, 0.94],
  },
};

export const slideInRightWithLayout = {
  initial: { opacity: 0, x: 100 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -100 },
};

export const slideInLeft = {
  initial: { opacity: 0, x: -100 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

export const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.9 },
};

export const springTransition = {
  type: "spring",
  stiffness: 300,
  damping: 30,
};

// ============================================================================
// EXPORT ALL
// ============================================================================

export default {
  // Modal
  ModalOverlay,
  Modal,
  ModalContent,
  ModalContentWrapper,
  ModalTitle,
  ModalSubtitle,

  // Progress
  ProgressBarContainer,
  ProgressBarFill,

  // Typography
  SectionTitle,
  SectionSubtitle,

  // Inputs
  Input,
  InputGroup,
  InputLabel,
  InputError,

  // Buttons
  SecondaryButton,
  ButtonGroup,

  // Chips
  ChipGroup,
  Chip,

  // Selection
  SelectionGrid,
  SelectionButton,

  // Invitation
  InvitationCard,
  InvitationIcon,
  InvitationContent,
  InvitationTitle,
  InvitationSubtitle,
  SelectedInvitationCard,
  BackToCreateButton,

  // Link Share
  LinkShareContainer,
  LinkShareHeader,
  LinkShareIcon,
  LinkShareText,
  LinkShareTitle,
  LinkShareDescription,
  LinkInputGroup,
  LinkInput,
  CopyButton,
  Divider,

  // Email Input
  EmailInputContainer,
  EmailInputRow,
  AddEmailButton,

  // Loading
  LoadingContainer,
  LoadingIcon,
  LoadingText,
  LoadingSteps,
  LoadingStep,
  LoadingStepIcon,

  // Info
  InfoMessage,

  // Phone Input
  PhoneInputWrapper,

  // Error Screen
  ErrorContainer,
  ErrorContent,
  ErrorIcon,
  ErrorTitle,
  ErrorMessage,
  ErrorActions,
  RetryButton,
  SkipButton,
  HelpText,

  // Animations
  fadeInOut,
  slideInRight,
  slideInLeft,
  scaleIn,
  springTransition,
  containerTransition,
  StepContent,
};
